"use client";

import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

/** Outer card for auth forms — clean, flat, single surface (no nested boxes). */
const AUTH_FORM_PANEL_CLASS =
  "rounded-2xl border border-border bg-card p-6 shadow-xl shadow-black/[0.04]";

export function AuthFormPanel({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={cn(AUTH_FORM_PANEL_CLASS, "space-y-5", className)}>{children}</div>;
}

export function AuthSkipLink({
  href,
  children,
  className,
}: {
  href: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <a
      href={href}
      className={cn(
        "sr-only focus:not-sr-only focus:absolute focus:z-50 focus:rounded-md focus:border focus:border-border focus:bg-card focus:px-3 focus:py-2 focus:text-sm focus:shadow-md focus:left-6 focus:top-28",
        className
      )}
    >
      {children}
    </a>
  );
}

/**
 * Groups one label + control + helper. Flat by design — no border, no fill —
 * so fields read as a single clean column (per Design System §14). `variant`
 * only tunes the vertical rhythm: roomier on full pages, denser in the modal.
 */
export function AuthFieldShell({
  children,
  className,
  variant = "page",
}: {
  children: React.ReactNode;
  className?: string;
  /** page: full-page spacing · modal: denser dialog spacing */
  variant?: "page" | "modal";
}) {
  return (
    <div className={cn(variant === "page" ? "space-y-1.5" : "space-y-1", className)}>
      {children}
    </div>
  );
}

/**
 * Keyboard hints — desktop only. Touch users have no Tab/Enter affordance, so
 * these render nothing on mobile (≤768px).
 */
export function AuthKeyboardHints({ className }: { className?: string }) {
  const isMobile = useIsMobile();
  if (isMobile) return null;
  return (
    <p className={cn("text-center text-label text-muted-foreground", className)}>
      <kbd className="rounded border border-border bg-muted px-1 py-0.5 font-mono text-2xs">Tab</kbd>{" "}
      moves between fields ·{" "}
      <kbd className="rounded border border-border bg-muted px-1 py-0.5 font-mono text-2xs">Enter</kbd>{" "}
      submits
    </p>
  );
}
